import { type DOMAttributes, type MouseEvent, type ReactElement, useCallback, useEffect, useRef } from 'react';
import cn from 'clsx';
import Tooltip from '@ubnt/ui-components/Tooltip/Tooltip';
import Button from '@ubnt/ui-components/Button/Button';
import AddIcon from '@ubnt/icons/AddPrimaryIcon';
import TrashIcon from '@ubnt/icons/TrashIcon';
import { useClickAway, useTimeoutFn } from 'react-use';
import { dispatcher, EditorEventType } from 'modules/editor/events';
import { useEditorStore } from 'modules/editor/store';
import { useApplicationStore } from 'modules/application/store';
import { Flex } from 'components/Flex';
import { MESH_AP_ID } from 'modules/devices/constants';
import { Html } from 'modules/editor/components/Html';

import classes from './ContextMenu.module.scss';

export type ActionType = 'addMesh' | 'deleteMesh';
export type Action = {
  icon: ReactElement;
  className?: string;
};
export type ActionMap = Record<ActionType, Action>;
export type Actions = {
  action: ActionType;
  onClick: NonNullable<DOMAttributes<HTMLButtonElement>['onClick']>;
  tooltip: string;
}[];

const ACTIONS: ActionMap = {
  addMesh: {
    icon: <AddIcon size="original" />,
  },
  deleteMesh: {
    icon: <TrashIcon size="original" />,
    className: classes.delete,
  },
};

const CLOSE_TIMEOUT = 500;

export const ContextMenu = (props: { actions: Actions }) => {
  const { actions = [] } = props;

  const contextMenu = useEditorStore((state) => state.contextMenu);
  const isTouchDevice = useApplicationStore((state) => state.isTouchDevice);

  const closeContextMenu = useCallback(
    () => useEditorStore.getState().updateContextMenu({ id: null, open: false }),
    []
  );

  const [, clear, reset] = useTimeoutFn(() => {
    closeContextMenu();
  }, CLOSE_TIMEOUT);

  const hoverDeviceId = useEditorStore((state) => state.hoverDeviceId);
  const hoverLinkId = useEditorStore((state) => state.hoverLinkId);

  useEffect(() => {
    if (!isTouchDevice) {
      if (contextMenu.id === hoverLinkId || contextMenu.id === hoverDeviceId) {
        clear();
      } else {
        reset();
      }
    }
  }, [reset, clear, hoverDeviceId, hoverLinkId, contextMenu.id, isTouchDevice]);

  const ref = useRef(null);
  useClickAway(ref, closeContextMenu);

  // close menu on zoom changes and on pointer missed event (no mesh object was hit)
  useEffect(() => {
    dispatcher.addEventListener(EditorEventType.ZOOM_CHANGED, closeContextMenu);

    return () => {
      dispatcher.removeEventListener(EditorEventType.ZOOM_CHANGED, closeContextMenu);
    };
  }, [closeContextMenu]);

  if (!contextMenu.open || !contextMenu.position || !actions.length) {
    return null;
  }

  return (
    <Html position={contextMenu.position}>
      <div
        ref={ref}
        className={cn(classes.ContextMenu, { [classes.bottom24]: contextMenu.id === MESH_AP_ID })}
        onPointerOver={clear}
        onPointerOut={reset}
      >
        <Flex alignCenter justifyCenter>
          {actions.map(({ action, onClick, tooltip }) => (
            <Tooltip key={action} description={tooltip} noWrap portal>
              <Button
                motif="dark"
                className={cn(classes.button, ACTIONS[action].className)}
                Icon={ACTIONS[action].icon}
                onClick={(e: MouseEvent<HTMLButtonElement>) => {
                  onClick(e);
                  closeContextMenu();
                }}
              />
            </Tooltip>
          ))}
        </Flex>
      </div>
    </Html>
  );
};
